import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { SlicePipe } from '@angular/common';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { mergeAll, tap } from 'rxjs/operators';

import { FormControl, FormGroup } from '@angular/forms';
import { MasterVehicleDataSource } from '../../data-sources/master-vehicle.data-source';
import { IBreadcrumbData } from '../../../../interfaces/breadcrumb-data.interface';
import { BarrierProfileService } from '../../services/barrier-profile.service';
import { EditComponent } from '../../components/edit/edit.component';
import { MasterVehicle } from '../../entities/master-vehicle';
import { SimpleDialogService } from '../../../../services/simple-dialog.service';
import { MasterVehicleService } from '../../services/master-vehicle.service';
import { SimpleDialogData } from '../../../../interfaces/simple-dialog-data.interface';
import { of } from 'rxjs';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class ListComponent implements OnInit, AfterViewInit {
  /**
   * Displayed columns array
   */
  displayedColumns: string[] = [
    'plate',
    'owner',
    'model',
    'card',
    'allowed',
    // 'exact',
    'barrierProfileName',
    'action',
  ];

  /**
   * Table config array
   */
  tableConfig: any[] = [
    {
      columnName: 'Matrícula',
    },
    {
      columnName: 'Propietario',
      pipe: new SlicePipe(),
      configPipe: [0, 50],
    },
    {
      columnName: 'Modelo',
    },
    {
      columnName: 'Tarjeta',
    },
    {
      columnName: 'Autoritzado',
    },
    // {
    //   columnName: 'Exacta'
    // },
    {
      columnName: 'Perfil Barrera',
    },
    '',
  ];

  /**
   * MasterVehicle data source
   */
  masterVehicleDataSource: MasterVehicleDataSource;

  /**
   * Paginator
   */
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /**
   * Sort
   */
  @ViewChild(MatSort) sort: MatSort;

  /**
   * Filter object
   */
  filter: any;

  /**
   * Count of total master vehicles
   */
  totalMasterVehicles: number;

  /**
   * BreadcrumbData
   */
  breadcrumbData: IBreadcrumbData;

  /**
   * Filter form
   */
  filterForm = new FormGroup({
    /**
     * plate
     */
    plate: new FormControl(),
    /**
     * Card
     */
    card: new FormControl(),
    /**
     * Owner
     */
    owner: new FormControl(),
    /**
     * Barrier profile id
     */
    barrierProfile: new FormControl(),
  });

  barrierProfiles: any[];

  /**
   * Constructor
   *
   * @param masterVehicleService MasterVehicleService
   * @param dialog MatDialog
   * @param simpleDialogService SimpleDialogService
   */
  constructor(
    private masterVehicleService: MasterVehicleService,
    private barrierProfileService: BarrierProfileService,
    public dialog: MatDialog,
    private simpleDialogService: SimpleDialogService
  ) {
    this.filter = {};

    this.breadcrumbData = {
      titles: {
        icon: 'directions_car',
        links: [
          {
            text: 'Lista Vehículos maestros',
          },
        ],
      },
      description: 'Aquí puede ver todos los vehículos maestros y puedes filtrarlos',
      buttons: [
        {
          text: 'Crear',
          action: () => this.create(),
          icon: 'add',
          disabled: false
        },
      ],
    };
  }

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    this.masterVehicleDataSource = new MasterVehicleDataSource(this.masterVehicleService);
    this.masterVehicleDataSource.load();
    this.masterVehicleService.getCount().subscribe((data: any) => (this.totalMasterVehicles = data.count));

    this.filterForm.valueChanges.subscribe((value) => {
      if (value.plate != null) {
        this.filter.plate = value.plate;
      }
      if (value.card != null) {
        this.filter.card = value.card;
      }
      if (value.owner != null) {
        this.filter.owner = value.owner;
      }
      if (value.barrierProfile != null) {
        this.filter.barrierProfile = value.barrierProfile;
      }

      this.loadMasterVehicles();
    });
  }

  /**
   * ngAfterViewInit
   */
  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    this.barrierProfileService.list().subscribe((data: any[]) => {
      this.barrierProfiles = data;
    });

    of(this.sort.sortChange, this.paginator.page)
      .pipe(
        mergeAll(),
        tap(() => this.loadMasterVehicles())
      )
      .subscribe();
  }

  /**
   * Create
   */
  create(): void {
    const dialogRef = this.dialog.open(EditComponent, { width: '450px', disableClose: true });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadMasterVehicles();
      }
    });
  }

  /**
   * Edit
   *
   * @param masterVehicle MasterVehicle instance
   */
  edit(masterVehicle: MasterVehicle): void {
    const dialogRef = this.dialog.open(EditComponent, {
      width: '450px',
      disableClose: true,
      data: {
        masterVehicleId: masterVehicle.plate,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadMasterVehicles();
      }
    });
  }

  /**
   * Remove
   *
   * @param masterVehicle MasterVehicle instance
   */
  remove(masterVehicle: MasterVehicle): void {
    const data: SimpleDialogData = {
      title: 'Warning!',
      message: `Do you want remove vehicle ${masterVehicle.plate}?`,
      rightButtonText: 'Yes',
    };

    this.simpleDialogService.open(data).subscribe((response) => {
      if (response) {
        this.masterVehicleService.delete(masterVehicle.plate).subscribe(() => this.loadMasterVehicles());
      }
    });
  }

  /**
   * Load master vehicles
   */
  loadMasterVehicles(): void {
    this.masterVehicleDataSource.load(this.sort.active, this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex, this.filter);

    this.masterVehicleService
      .getCount('plate', this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex, this.filter)
      //.subscribe((data: { count: number }) => (this.totalMasterVehicles = data.count)); //TODO: XGC check this
      .subscribe((data: any) => (this.totalMasterVehicles = data.count));
  }

  clear() {
    this.filterForm.reset();
    this.filter = {};
    this.loadMasterVehicles();
    this.paginator.pageIndex = 0;
  }
}
